import styled from 'styled-components';
import { FeatureId } from '../../../../../common/iam/access/feature-access/types';
import { NotAvailableContent } from '../../../upright/iam/access/feature-access/not-available-content';
import {
  NotAvailableCommonElements,
  NotAvailableContentType,
} from '../../../upright/iam/access/feature-access/not-available-content/types';
import { themeGet } from '../../theme/custom-theme';
import { ReactNode } from 'react';
import { Text } from '@upright/ui/common/generic/components/primer-custom/text';

interface Props {
  featureId: FeatureId;
  customContent?: NotAvailableCommonElements;
  onDismiss?: () => void;
  /**
   * PLT-4790: when set, replaces the default diagonal tile pattern with a flat
   * solid color (used by the PLG Enterprise draft-mode paywall, which wants a
   * clean #BCBDFF surface rather than the subscription-paywall tile pattern).
   */
  backgroundColor?: string;
}

export function FeatureAccessEmptyState({ featureId, customContent, onDismiss, backgroundColor }: Props) {
  return (
    <EmptyState onDismiss={onDismiss} backgroundColor={backgroundColor}>
      <EmptyStateIcon>
        <NotAvailableContent
          contentType={NotAvailableContentType.ICON}
          featureId={featureId}
          customContent={customContent}
        />
      </EmptyStateIcon>
      <EmptyStateTitle>
        <NotAvailableContent
          contentType={NotAvailableContentType.TITLE}
          featureId={featureId}
          customContent={customContent}
        />
      </EmptyStateTitle>
      <EmptyStateIngress>
        <NotAvailableContent
          contentType={NotAvailableContentType.INGRESS}
          featureId={featureId}
          customContent={customContent}
        />
      </EmptyStateIngress>
    </EmptyState>
  );
}

/**
 * A generic component for displaying an empty state, i.e. when data or access is missing.
 * If you need to display an empty state related to feature access, @see FeatureAccessEmptyState.
 * @example
 *  <EmptyState>
 *    <EmptyStateIcon>
 *      <NotAvailableIcon />
 *    </EmptyStateIcon>
 *    <EmptyStateTitle>Missing Data</EmptyStateTitle>
 *    <EmptyStateIngress>Financial materiality data could not be loaded. Please try again later.</EmptyStateIngress>
 *  </EmptyState>
 */
export function EmptyState({
  children,
  onDismiss,
  backgroundColor,
}: {
  children: ReactNode;
  onDismiss?: () => void;
  backgroundColor?: string;
}) {
  return (
    <Wrapper $backgroundColor={backgroundColor}>
      {onDismiss && (
        <DismissButton onClick={onDismiss} aria-label="Dismiss">
          ✕
        </DismissButton>
      )}
      <Content>{children}</Content>
    </Wrapper>
  );
}

export function EmptyStateIcon({ children }: { children: ReactNode }) {
  return <FluidIcon>{children}</FluidIcon>;
}

export function EmptyStateTitle({ children }: { children: ReactNode }) {
  return <Text fontWeight="bold">{children}</Text>;
}

export function EmptyStateIngress({ children }: { children: ReactNode }) {
  return <div>{children}</div>;
}

const Wrapper = styled.section<{ $backgroundColor?: string }>`
  position: relative;
  display: grid;
  place-items: center;
  text-align: center;
  height: 100%;
  padding: ${themeGet('space.6')};
  ${themeGet('space.8')};
  border-radius: ${themeGet('radii.2')};
  /* PLT-4790: a solid $backgroundColor (e.g. the draft-mode paywall's
     #BCBDFF) opts out of the default diagonal tile pattern. */
  ${props =>
    props.$backgroundColor
      ? `background: ${props.$backgroundColor};`
      : `background:
    linear-gradient(
      135deg,
      #f9f9f9 21px,
      ${themeGet('colors.facade.bgGray')(props)} 22px,
      ${themeGet('colors.facade.bgGray')(props)} 24px,
      transparent 24px,
      transparent 67px,
      ${themeGet('colors.facade.bgGray')(props)} 67px,
      ${themeGet('colors.facade.bgGray')(props)} 69px,
      transparent 69px
    ),
    linear-gradient(
        225deg,
        #f9f9f9 21px,
        ${themeGet('colors.facade.bgGray')(props)} 22px,
        ${themeGet('colors.facade.bgGray')(props)} 24px,
        transparent 24px,
        transparent 67px,
        ${themeGet('colors.facade.bgGray')(props)} 67px,
        ${themeGet('colors.facade.bgGray')(props)} 69px,
        transparent 69px
      )
      0 64px;
  background-color: #f9f9f9;
  background-size: 64px 128px;`}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${themeGet('space.4')};
  max-width: calc(${themeGet('space.13')} * 2);
`;

const FluidIcon = styled.div`
  width: clamp(40px, calc(5vmin + 20px), 60px);
  aspect-ratio: 1;
`;

const DismissButton = styled.button`
  position: absolute;
  top: ${themeGet('space.3')};
  right: ${themeGet('space.3')};
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${themeGet('colors.fg.default')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${themeGet('radii.1')};
  font-size: 16px;
  font-weight: 900;
  padding: 0;

  &:hover {
    background: ${themeGet('colors.bg.gray')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focus')};
  }
`;
